import type { CatalogueAgent } from "@/components/WorkflowPlayground/PlaygroundAgentCatalogue";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ReactFlowProvider } from "@xyflow/react";

import { useWorkflowStore, type Workflow } from "@/stores/WorkflowStore";
import { usePlaygroundStore } from "@/stores/PlaygroundStore";
import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import {
  SSEEventType,
  isCompletionEvent,
  isMessageCompletedEvent,
  isStreamingEvent,
  extractMessageId,
} from "@/types/sse";
import {
  PlaygroundCanvas,
  PlaygroundToolbar,
  PlaygroundPropertiesPanel,
  PlaygroundWorkflowSelector,
  PlaygroundUnsavedChangesModal,
  PlaygroundMessagesPanel,
  PlaygroundProgressTimeline,
  PlaygroundAgentCatalogue,
} from "@/components/WorkflowPlayground";
import { createBlankWorkflow } from "@/utils/playground";
import { logger } from "@/telemetry";

export const WorkflowPlaygroundPage: React.FC = () => {
  const { workflowId } = useParams<{ workflowId?: string }>();
  const navigate = useNavigate();
  const { workflow, loadWorkflow, saveWorkflow, setWorkflow } = useWorkflowStore();
  const { adminClient, platformClient, sseWorkflowClient } = useWebuildAPIClient();
  const {
    hasUnsavedChanges,
    isRunning,
    snapshotWorkflow,
    checkUnsavedChanges,
    markClean,
    setIsRunning,
    resetExecution,
    updateNodeStatus,
    resetNodeStatuses,
    addMessage,
    updateMessage,
    setWorkflowProgress,
    setMessagesPanelOpen,
    reset: resetPlayground,
  } = usePlaygroundStore();

  const [isSaving, setIsSaving] = useState(false);
  const [showWorkflowSelector, setShowWorkflowSelector] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [showAgentCatalogue, setShowAgentCatalogue] = useState(false);
  const [pendingWorkflowId, setPendingWorkflowId] = useState<string | null | undefined>(undefined);

  const autoLayoutRef = useRef<(() => void) | null>(null);

  // SSE connection tracking
  const runIdRef = useRef<string | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // ─── SSE cleanup ─────────────────────────────────────────────────────────

  const cleanupSSE = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    runIdRef.current = null;
  }, []);

  // Load workflow from URL params
  useEffect(() => {
    if (workflowId && adminClient) {
      loadWorkflow(workflowId, adminClient)
        .then(() => {
          const loaded = useWorkflowStore.getState().workflow;

          snapshotWorkflow(loaded);
        })
        .catch((error) => {
          logger.error("Failed to load workflow", error);
        });
    } else if (!workflowId) {
      // New workflow - set a blank template
      const blank = createBlankWorkflow();

      setWorkflow(blank as Workflow);
      snapshotWorkflow(blank as Workflow);
    }

    return () => {
      cleanupSSE();
      resetPlayground();
      // Clear WorkflowStore so stale data doesn't leak to other pages (e.g. Chat)
      setWorkflow(null);
    };
  }, [
    workflowId,
    adminClient,
    cleanupSSE,
    loadWorkflow,
    resetPlayground,
    setWorkflow,
    snapshotWorkflow,
  ]);

  // Track unsaved changes
  useEffect(() => {
    checkUnsavedChanges(workflow);
  }, [workflow, checkUnsavedChanges]);

  // ─── Run workflow ────────────────────────────────────────────────────────

  const handleRun = useCallback(async () => {
    if (!workflow || !platformClient || !sseWorkflowClient || isRunning) return;

    // Reset previous execution state
    resetExecution();
    setIsRunning(true);
    setMessagesPanelOpen(true);

    // Build step order for progress tracking — only top-level nodes
    // (matching the canvas which excludes team member agents)
    const teamIds = new Set(Object.keys(workflow.teams || {}));
    const teamMemberIds = new Set<string>();
    const memberToTeam = new Map<string, string>();

    if (workflow.teams) {
      Object.entries(workflow.teams).forEach(([teamId, team]) => {
        (team.members || []).forEach((memberId: string) => {
          if (!teamIds.has(memberId)) {
            teamMemberIds.add(memberId);
            memberToTeam.set(memberId, teamId);
          }
        });
      });
    }

    const topLevelAgentIds = Object.keys(workflow.agents || {}).filter(
      (id) => !teamMemberIds.has(id),
    );
    const allStepIds = [...topLevelAgentIds, ...Array.from(teamIds)];

    resetNodeStatuses(allStepIds);
    setWorkflowProgress({
      totalSteps: allStepIds.length,
      completedSteps: 0,
      currentStep: null,
      stepOrder: allStepIds,
      executionOrder: allStepIds,
    });

    try {
      // 1. Create a workflow run
      logger.info("Creating workflow run");
      const response = await platformClient.workflows.create({
        createWorkflowReq: { workflow: workflow as any },
      });

      const runId = response.workflowId;

      if (!runId) {
        throw new Error("No workflow run ID returned");
      }

      runIdRef.current = runId;
      logger.info("Workflow run created", { runId });

      // 2. Subscribe to SSE BEFORE invoking (critical ordering)
      const abortController = new AbortController();

      abortControllerRef.current = abortController;

      const es = sseWorkflowClient(runId);

      if (!es) {
        throw new Error("Failed to create SSE connection");
      }

      eventSourceRef.current = es;

      // Destructure signal for cleaner abort checks (matches ChatStore pattern)
      const { signal } = abortController;

      // Track which messages we've seen (for accumulation)
      const messageChunks = new Map<string, string>();
      const messageAgents = new Map<string, string>();
      let completedStepsCount = 0;

      // ─── SSE Event Handlers (aligned with ChatStore.subscribe) ────

      const handleOutput = (event: MessageEvent) => {
        if (signal.aborted) return;

        let msg: any;

        try {
          msg = JSON.parse(event.data);
        } catch {
          logger.error("Failed to parse SSE message");

          return;
        }

        // Simple data extraction — matches ChatStore exactly
        const data = msg.data || msg.content || "";
        const agent = msg.agent;
        const messageId = extractMessageId(msg);

        if (!messageId) return;

        // Validate streaming events have required data
        if (isStreamingEvent(msg) && !data) return;

        // Skip empty non-completion events
        if (!data && !isCompletionEvent(msg)) return;

        // Filter system/debug messages (same filter as ChatStore)
        if (
          data &&
          (data.includes("Direct call completed") ||
            data.includes("response via agent reply mechanism") ||
            data.includes("agent reply mechanism") ||
            data.match?.(/^(System:|Debug:|Status:)/i))
        ) {
          return;
        }

        // Update node status to "running" when we first see output from an agent
        // For team members, resolve to the parent team node
        if (agent) {
          const nodeId = memberToTeam.get(agent) || agent;
          const currentStatus = usePlaygroundStore.getState().nodeStatuses[nodeId];

          if (currentStatus !== "running" && currentStatus !== "success") {
            updateNodeStatus(nodeId, "running");
            setWorkflowProgress({ currentStep: nodeId });
          }
          messageAgents.set(messageId, nodeId);
        }

        // Accumulate message content (same pattern as ChatStore)
        const existingContent = messageChunks.get(messageId) || "";
        const newContent = existingContent + data;

        messageChunks.set(messageId, newContent);

        if (signal.aborted) return;

        // Check if message already exists in store
        const existingMessages = usePlaygroundStore.getState().executionMessages;
        const existingMsg = existingMessages.find((m) => m.id === messageId);

        if (existingMsg) {
          updateMessage(messageId, { content: newContent });
        } else {
          addMessage({
            id: messageId,
            role: "assistant",
            content: newContent,
            status: "streaming",
            agent: agent || undefined,
          });
        }
      };

      const finalizeMessage = (
        event: MessageEvent,
        status: "success" | "failed" | "stopped" = "success",
      ) => {
        if (signal.aborted) return;

        let msg: any;

        try {
          msg = JSON.parse(event.data);
        } catch {
          return;
        }

        // For message_completed events, the actual message data is in the 'data' field
        // (matches ChatStore finalizeMessage pattern)
        let messageData = msg;

        if (isMessageCompletedEvent(msg) && msg.data) {
          messageData = msg.data;
        }

        const data = messageData.data || "";
        const agent = messageData.agent;
        const messageId = extractMessageId(messageData);

        if (!messageId) return;

        if (signal.aborted) return;

        const existingMessages = usePlaygroundStore.getState().executionMessages;
        const existingMsg = existingMessages.find((m) => m.id === messageId);

        if (existingMsg) {
          // Preserve existing content if new data is empty (matches ChatStore)
          const finalContent = data || existingMsg.content;

          updateMessage(messageId, {
            content: finalContent,
            status,
            usage: messageData.usage
              ? {
                  promptTokens:
                    messageData.usage.prompt_tokens || messageData.usage.promptTokens || 0,
                  completionTokens:
                    messageData.usage.completion_tokens || messageData.usage.completionTokens || 0,
                  totalTokens: messageData.usage.total_tokens || messageData.usage.totalTokens || 0,
                }
              : undefined,
          });
        } else {
          // Create new completed message
          addMessage({
            id: messageId,
            role: "assistant",
            content: data,
            status,
            agent: agent || undefined,
            usage: messageData.usage
              ? {
                  promptTokens:
                    messageData.usage.prompt_tokens || messageData.usage.promptTokens || 0,
                  completionTokens:
                    messageData.usage.completion_tokens || messageData.usage.completionTokens || 0,
                  totalTokens: messageData.usage.total_tokens || messageData.usage.totalTokens || 0,
                }
              : undefined,
          });
        }

        // Mark the agent/team node as complete
        const rawAgent = agent || (messageId ? messageAgents.get(messageId) : undefined);
        const resolvedNodeId = rawAgent ? memberToTeam.get(rawAgent) || rawAgent : undefined;

        if (resolvedNodeId) {
          updateNodeStatus(resolvedNodeId, status === "success" ? "success" : "error");
          completedStepsCount++;
          setWorkflowProgress({ completedSteps: completedStepsCount });
        }
      };

      const handleWorkflowCompletion = (event: MessageEvent) => {
        if (signal.aborted) return;

        logger.info("Workflow execution complete");

        // Parse event to check for stopped/cancelled status (matches ChatStore)
        let eventData: any = {};

        try {
          eventData = JSON.parse(event.data);
        } catch {
          // Use default
        }

        const workflowStatus = eventData.status;

        if (workflowStatus === "stopped" || workflowStatus === "cancelled") {
          finalizeMessage(event, "stopped");

          // Mark remaining running nodes as stopped
          const statuses = usePlaygroundStore.getState().nodeStatuses;

          Object.entries(statuses).forEach(([nodeId, nodeStatus]) => {
            if (nodeStatus === "running") {
              updateNodeStatus(nodeId, "error");
            }
          });

          setIsRunning(false);
          cleanupSSE();

          return;
        }

        finalizeMessage(event, "success");

        // Mark all remaining running nodes as success
        const statuses = usePlaygroundStore.getState().nodeStatuses;

        Object.entries(statuses).forEach(([nodeId, nodeStatus]) => {
          if (nodeStatus === "running") {
            updateNodeStatus(nodeId, "success");
          }
        });

        setWorkflowProgress({
          completedSteps: allStepIds.length,
          currentStep: null,
        });
        setIsRunning(false);
        cleanupSSE();
      };

      const handleMessageCompletion = (event: MessageEvent) => {
        if (signal.aborted) return;

        finalizeMessage(event, "success");
      };

      const handleError = (event: MessageEvent) => {
        if (signal.aborted) return;
        if (!event.data) return;

        let errorMessage = "Workflow execution error";

        try {
          const msg = JSON.parse(event.data);

          errorMessage = msg.data?.message || msg.data?.error || msg.data || errorMessage;
        } catch {
          // Use default error message
        }

        logger.error("SSE error event", { error: String(errorMessage) });

        // Mark current running nodes as error
        const statuses = usePlaygroundStore.getState().nodeStatuses;

        Object.entries(statuses).forEach(([nodeId, nodeStatus]) => {
          if (nodeStatus === "running") {
            updateNodeStatus(nodeId, "error");
          }
        });

        // Add error message to panel
        addMessage({
          id: `error-${Date.now()}`,
          role: "assistant",
          content: typeof errorMessage === "string" ? errorMessage : JSON.stringify(errorMessage),
          status: "failed",
        });

        setIsRunning(false);
        cleanupSSE();
      };

      // Register all event listeners (matches ChatStore ordering)
      es.addEventListener(SSEEventType.MESSAGE, handleOutput);
      es.addEventListener(SSEEventType.OUTPUT, handleOutput);
      es.addEventListener(SSEEventType.WORKFLOW_COMPLETED, handleWorkflowCompletion);
      es.addEventListener(SSEEventType.MESSAGE_COMPLETED, handleMessageCompletion);
      es.addEventListener(SSEEventType.ERROR, handleError);

      es.onerror = () => {
        if (signal.aborted) return;

        if (es.readyState === EventSource.CLOSED) {
          logger.info("SSE connection closed");

          if (usePlaygroundStore.getState().isRunning) {
            setIsRunning(false);
            cleanupSSE();
          }
        }
      };

      // 3. Build invoke data from workflow input values
      const invokeData: Record<string, any> = {};
      let hasUserInput = false;

      if (workflow.input) {
        Object.entries(workflow.input).forEach(([paramName, paramDef]: [string, any]) => {
          if (paramDef?.value) {
            invokeData[paramName] = paramDef.value;
            hasUserInput = true;
          }
        });
      }

      // Only use "start" as a fallback trigger when no input values were defined
      if (!hasUserInput) {
        invokeData.prompt = "start";
      }

      // 3b. Include uploaded files (for chat_agent workflows)
      const { uploadedFiles } = usePlaygroundStore.getState();

      if (uploadedFiles.length > 0) {
        invokeData.files = uploadedFiles.map((f) => ({
          id: f.id,
          name: f.name,
          type: f.type,
          size: f.size,
        }));
      }

      // 4. Invoke the workflow (AFTER subscribing)
      logger.info("Invoking workflow", { runId, invokeData: JSON.stringify(invokeData) });
      await platformClient.workflows.invoke({
        workflowId: runId,
        invokeWorkflowReq: { data: invokeData },
      });

      logger.info("Workflow invoked successfully");
    } catch (error: any) {
      logger.error("Failed to run workflow", error);

      addMessage({
        id: `error-${Date.now()}`,
        role: "assistant",
        content: error?.body?.detail || error?.message || "Failed to start workflow execution",
        status: "failed",
      });

      setIsRunning(false);
      cleanupSSE();
    }
  }, [
    workflow,
    platformClient,
    sseWorkflowClient,
    isRunning,
    resetExecution,
    setIsRunning,
    setMessagesPanelOpen,
    resetNodeStatuses,
    setWorkflowProgress,
    updateNodeStatus,
    addMessage,
    updateMessage,
    cleanupSSE,
  ]);

  // ─── Stop workflow ───────────────────────────────────────────────────────

  const handleStop = useCallback(async () => {
    if (!platformClient || !runIdRef.current) return;

    try {
      logger.info("Stopping workflow execution", { runId: runIdRef.current });
      await platformClient.workflows.stopStreaming({
        workflowId: runIdRef.current,
        discardMessages: false,
      });
    } catch (error) {
      logger.error("Failed to stop workflow", error);
    }

    // Mark running nodes as stopped
    const statuses = usePlaygroundStore.getState().nodeStatuses;

    Object.entries(statuses).forEach(([nodeId, status]) => {
      if (status === "running") {
        updateNodeStatus(nodeId, "error");
      }
    });

    // Update streaming messages to stopped
    const messages = usePlaygroundStore.getState().executionMessages;

    messages.forEach((msg) => {
      if (msg.status === "streaming") {
        updateMessage(msg.id, { status: "stopped" });
      }
    });

    setIsRunning(false);
    cleanupSSE();
  }, [platformClient, updateNodeStatus, updateMessage, setIsRunning, cleanupSSE]);

  // ─── Save ──────────────────────────────────────────────────────────────────

  const handleSave = useCallback(async () => {
    if (!workflow || !adminClient) return;

    setIsSaving(true);
    try {
      await saveWorkflow(adminClient);
      snapshotWorkflow(workflow);
      markClean();
      logger.info("Workflow saved successfully");

      // If this was a new workflow and it now has an ID, navigate to it
      if (!workflowId && workflow.id) {
        navigate(`/workflows/playground/${workflow.id}`, { replace: true });
      }
    } catch (error) {
      logger.error("Failed to save workflow", error);
    } finally {
      setIsSaving(false);
    }
  }, [workflow, adminClient, saveWorkflow, snapshotWorkflow, markClean, workflowId, navigate]);

  // ─── Add nodes ─────────────────────────────────────────────────────────────

  const handleAddAgent = useCallback(() => {
    if (!workflow) return;

    const existingAgentIds = Object.keys(workflow.agents || {});
    const isFirstAgent = existingAgentIds.length === 0;

    const agentId = `agent_${Date.now()}`;

    // First agent gets a trigger from input (prompt→prompt);
    // subsequent agents get a trigger from the last existing agent (prompt→data)
    const trigger = isFirstAgent
      ? { source: "input", mapping: { prompt: "prompt" } }
      : { source: existingAgentIds[existingAgentIds.length - 1], mapping: { prompt: "data" } };

    const updatedWorkflow = {
      ...workflow,
      // Ensure input definition exists
      input:
        workflow.input && Object.keys(workflow.input).length > 0
          ? workflow.input
          : { prompt: { type: "string", description: "User prompt" } },
      agents: {
        ...workflow.agents,
        [agentId]: {
          type: "chat_agent",
          parameters: {
            model: "gpt-4.1",
            system_message: "You are a helpful assistant.",
            temperature: "0.7",
            max_tokens: "2000",
          },
          triggers: [trigger],
        },
      },
    };

    setWorkflow(updatedWorkflow);
  }, [workflow, setWorkflow]);

  const handleImportAgent = useCallback(
    (catalogueAgent: CatalogueAgent) => {
      if (!workflow) return;

      const existingAgentIds = Object.keys(workflow.agents || {});

      // Generate a unique agent ID based on the original name
      let agentId = catalogueAgent.agentId;
      let suffix = 2;

      while (workflow.agents[agentId]) {
        agentId = `${catalogueAgent.agentId}_${suffix}`;
        suffix++;
      }

      const isFirstAgent = existingAgentIds.length === 0;

      // Auto-trigger: same logic as handleAddAgent
      const trigger = isFirstAgent
        ? { source: "input", mapping: { prompt: "prompt" } }
        : { source: existingAgentIds[existingAgentIds.length - 1], mapping: { prompt: "data" } };

      const updatedWorkflow = {
        ...workflow,
        input:
          workflow.input && Object.keys(workflow.input).length > 0
            ? workflow.input
            : { prompt: { type: "string", description: "User prompt" } },
        agents: {
          ...workflow.agents,
          [agentId]: {
            type: catalogueAgent.config.type,
            parameters: { ...catalogueAgent.config.parameters },
            triggers: [trigger],
            description: catalogueAgent.config.description,
            ...(catalogueAgent.config.parameters?.tools
              ? { tools: [...catalogueAgent.config.parameters.tools] }
              : {}),
            ...(catalogueAgent.config.userInteraction
              ? { userInteraction: catalogueAgent.config.userInteraction }
              : {}),
          },
        },
      };

      setWorkflow(updatedWorkflow);
    },
    [workflow, setWorkflow],
  );

  const handleAddTeam = useCallback(() => {
    if (!workflow) return;

    const teamId = `team_${Date.now()}`;
    const updatedWorkflow = {
      ...workflow,
      teams: {
        ...(workflow.teams || {}),
        [teamId]: {
          members: [],
          strategy: "selector" as const,
          maxCycles: 10,
          goal: "",
          useLlmJudgment: true,
          triggers: [],
          model: "gpt-4.1",
        },
      },
    };

    setWorkflow(updatedWorkflow);
  }, [workflow, setWorkflow]);

  const handleAutoLayout = useCallback(() => {
    autoLayoutRef.current?.();
  }, []);

  // ─── Workflow switching ────────────────────────────────────────────────────

  const handleOpenWorkflowSelector = useCallback(() => {
    setShowWorkflowSelector(true);
  }, []);

  const navigateToWorkflow = useCallback(
    (id: string | null) => {
      cleanupSSE();
      resetPlayground();
      setWorkflow(null);

      if (id) {
        navigate(`/workflows/playground/${id}`);
      } else {
        navigate("/workflows/playground");
      }
    },
    [navigate, resetPlayground, setWorkflow, cleanupSSE],
  );

  const handleWorkflowSelect = useCallback(
    (selectedId: string | null) => {
      if (hasUnsavedChanges) {
        setPendingWorkflowId(selectedId);
        setShowUnsavedChangesModal(true);
      } else {
        navigateToWorkflow(selectedId);
      }
    },
    [hasUnsavedChanges, navigateToWorkflow],
  );

  const handleSaveAndSwitch = useCallback(async () => {
    await handleSave();
    setShowUnsavedChangesModal(false);
    if (pendingWorkflowId !== undefined) {
      navigateToWorkflow(pendingWorkflowId);
      setPendingWorkflowId(undefined);
    }
  }, [handleSave, pendingWorkflowId, navigateToWorkflow]);

  const handleDiscardAndSwitch = useCallback(() => {
    setShowUnsavedChangesModal(false);
    if (pendingWorkflowId !== undefined) {
      navigateToWorkflow(pendingWorkflowId);
      setPendingWorkflowId(undefined);
    }
  }, [pendingWorkflowId, navigateToWorkflow]);

  const handleCancelSwitch = useCallback(() => {
    setShowUnsavedChangesModal(false);
    setPendingWorkflowId(undefined);
  }, []);

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <ReactFlowProvider>
        {/* Toolbar */}
        <PlaygroundToolbar
          isSaving={isSaving}
          onAddAgent={handleAddAgent}
          onAddTeam={handleAddTeam}
          onAutoLayout={handleAutoLayout}
          onOpenAgentCatalogue={() => setShowAgentCatalogue(true)}
          onOpenWorkflowSelector={handleOpenWorkflowSelector}
          onRun={handleRun}
          onSave={handleSave}
          onStop={handleStop}
        />

        {/* Progress timeline */}
        <PlaygroundProgressTimeline />

        {/* Main content area */}
        <div className="flex flex-1 min-h-0">
          {/* Canvas */}
          <div className="flex-1 flex flex-col min-h-0">
            <div className="flex-1 min-h-0">
              <PlaygroundCanvas onAutoLayout={autoLayoutRef} />
            </div>

            {/* Messages panel (bottom) */}
            <PlaygroundMessagesPanel />
          </div>

          {/* Properties panel */}
          <PlaygroundPropertiesPanel />
        </div>
      </ReactFlowProvider>

      {/* Modals */}
      <PlaygroundWorkflowSelector
        isOpen={showWorkflowSelector}
        onClose={() => setShowWorkflowSelector(false)}
        onSelect={handleWorkflowSelect}
      />

      <PlaygroundUnsavedChangesModal
        isOpen={showUnsavedChangesModal}
        isSaving={isSaving}
        workflowName={workflow?.name || ""}
        onCancel={handleCancelSwitch}
        onDiscardAndSwitch={handleDiscardAndSwitch}
        onSaveAndSwitch={handleSaveAndSwitch}
      />

      <PlaygroundAgentCatalogue
        isOpen={showAgentCatalogue}
        onClose={() => setShowAgentCatalogue(false)}
        onImport={handleImportAgent}
      />
    </div>
  );
};
